export type PhaseCategory = {
  icon: string
  title: string
  items: string[]
}

export type PhasePill = {
  label: string
}

export type PhaseColorStyles = {
  // Text colors
  text: string
  // Background colors
  bg: string
  bgLight: string
  bgAccent: string
  // Border colors
  border: string
  // Icon styles
  iconBg: string
  // Tag/pill styles
  tagBg: string
  tagText: string
  tagHoverBg: string
}

export type Phase = {
  id: string
  icon: string
  title: string
  subtitle: string
  color: "blue" | "yellow" | "green"
  colorClass: {
    text: string
    bgColor: string
  }
  description: string
  quote: string
  categories: PhaseCategory[]
  pills: PhasePill[]
}

// Centralized color mapping for all phase-related components
export const phaseColorMap: Record<
  "blue" | "yellow" | "green",
  PhaseColorStyles
> = {
  blue: {
    // Text colors
    text: "text-blue-900 dark:text-blue-200",
    // Background colors
    bg: "bg-blue-50 dark:bg-blue-950/30",
    bgLight: "bg-blue-50/50 dark:bg-blue-900/20",
    bgAccent: "bg-blue-100 dark:bg-blue-800/30",
    // Border colors
    border: "border-blue-200 dark:border-blue-800/40",
    // Icon styles
    iconBg: "bg-blue-100 dark:bg-blue-900/40",
    // Tag/pill styles
    tagBg: "bg-blue-100 dark:bg-blue-800/30",
    tagText: "text-blue-900 dark:text-blue-200",
    tagHoverBg: "hover:bg-blue-200 dark:hover:bg-blue-800/50",
  },
  yellow: {
    // Text colors
    text: "text-amber-900 dark:text-amber-200",
    // Background colors
    bg: "bg-amber-50 dark:bg-amber-950/30",
    bgLight: "bg-amber-50/50 dark:bg-amber-900/20",
    bgAccent: "bg-amber-100 dark:bg-amber-800/30",
    // Border colors
    border: "border-amber-200 dark:border-amber-800/40",
    // Icon styles
    iconBg: "bg-amber-100 dark:bg-amber-900/40",
    // Tag/pill styles
    tagBg: "bg-amber-100 dark:bg-amber-800/30",
    tagText: "text-amber-900 dark:text-amber-200",
    tagHoverBg: "hover:bg-amber-200 dark:hover:bg-amber-800/50",
  },
  green: {
    // Text colors
    text: "text-emerald-900 dark:text-emerald-200",
    // Background colors
    bg: "bg-emerald-50 dark:bg-emerald-950/30",
    bgLight: "bg-emerald-50/50 dark:bg-emerald-900/20",
    bgAccent: "bg-emerald-100 dark:bg-emerald-800/30",
    // Border colors
    border: "border-emerald-200 dark:border-emerald-800/40",
    // Icon styles
    iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
    // Tag/pill styles
    tagBg: "bg-emerald-100 dark:bg-emerald-800/30",
    tagText: "text-emerald-900 dark:text-emerald-200",
    tagHoverBg: "hover:bg-emerald-200 dark:hover:bg-emerald-800/50",
  },
}

// Phase data
export const phasesData: Phase[] = [
  {
    id: "plan",
    icon: "MapIcon",
    title: "Plan",
    subtitle: "Think Strategically. Act Intelligently.",
    color: "blue",
    colorClass: {
      text: "text-blue-900 dark:text-blue-200",
      bgColor: "bg-blue-600 dark:bg-blue-700",
    },
    description:
      "Before anything is built, we discover, assess, and align. The Plan phase lays the foundation for success by ensuring clear direction, strong governance, and meaningful business outcomes.",
    quote:
      "The Plan phase is where you prevent failure. Shortcut this, and the rest is just theater.",
    categories: [
      {
        icon: "EyeIcon",
        title: "Vision & Alignment",
        items: [
          "Define your vision: What role will your EKG play?",
          "Secure executive champions: Without C-level ownership, this will fail",
        ],
      },
      {
        icon: "CircleStackIcon",
        title: "Knowledge Discovery",
        items: [
          "Explore the Use Case Tree: Structure capabilities, outcomes, personas",
          "Assess data and tech readiness: Map brownfield systems, uncover siloes",
        ],
      },
      {
        icon: "ChartBarIcon",
        title: "Strategy & Planning",
        items: [
          "Identify your Lighthouse Use Case: A meaningful, governed proof point",
          "Craft a real business case: Financials, milestones, KPIs",
        ],
      },
    ],
    pills: [{ label: "Use Case Tree" }, { label: "Lighthouse Use Case" }],
  },
  {
    id: "build",
    icon: "WrenchScrewdriverIcon",
    title: "Build",
    subtitle: "Assemble the Semantic Core — Not Just Another Interface",
    color: "yellow",
    colorClass: {
      text: "text-amber-900 dark:text-amber-200",
      bgColor: "bg-amber-500 dark:bg-amber-600",
    },
    description:
      "This is where capabilities are forged — semantically, operationally, and securely. The Build phase transforms abstract plans into concrete semantic structures that enable both human and artificial intelligence.",
    quote:
      "You're not building a platform. You're building an architecture that thinks semantically.",
    categories: [
      {
        icon: "PuzzlePieceIcon",
        title: "Core Architecture",
        items: [
          "Deploy your EKG Service Layer: The federation and governance engine",
          "Build the Virtual EKG: Ontologies, workflows, and semantic structure",
        ],
      },
      {
        icon: "ArrowsRightLeftIcon",
        title: "Integration & Extension",
        items: [
          "Integrate brownfield systems via secure proxy stories",
          "Connect to existing data ecosystems without disruption",
        ],
      },
      {
        icon: "CpuChipIcon",
        title: "Semantic Automation",
        items: [
          "Deliver the first semantic workflows: Composable, agent-ready logic",
          "Launch governed agentic execution for GenAI agents and human personas",
        ],
      },
    ],
    pills: [{ label: "EKG Service Layer" }, { label: "Semantic Workflows" }],
  },
  {
    id: "run",
    icon: "RocketLaunchIcon",
    title: "Run",
    subtitle: "Enterprise-Grade, Production-Proven.",
    color: "green",
    colorClass: {
      text: "text-emerald-900 dark:text-emerald-200",
      bgColor: "bg-emerald-600 dark:bg-emerald-700",
    },
    description:
      "Most failures happen here — in production. We ensure they don't. The Run phase brings your semantic architecture to life in a secure, observable, and continuously improving manner.",
    quote:
      "This is where the promise is kept. Where real outcomes happen. And where agnos.ai leads.",
    categories: [
      {
        icon: "ShieldCheckIcon",
        title: "Secure Operations",
        items: [
          "Run GenAI agents safely: Scoped by use case, governed by story",
          "Deliver observability, security, and compliance by design",
        ],
      },
      {
        icon: "CircleStackIcon",
        title: "Data & Knowledge Pipeline",
        items: [
          "Operate your Data Supply Chains with SLAs and lineage",
          "Link Data Products to Use Cases through semantic bridges",
        ],
      },
      {
        icon: "ArrowPathIcon",
        title: "Continuous Evolution",
        items: [
          "Continuously improve via semantic feedback loops",
          "Provide optimization and regeneration services as the enterprise evolves",
        ],
      },
    ],
    pills: [{ label: "Data Products" }, { label: "Provenance" }],
  },
]
